import ClearIcon from '@mui/icons-material/Clear';
import {
  Button,
  IconButton,
  Popover,
  PopoverProps,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  advSearchAnchorOrigin,
  advSearchElevation,
  advSearchTransformOrigin,
  getAdvancedPopoverFooterAreaSx,
  getAdvancedPopoverHeaderAreaSx,
  getAdvancedPopoverInputsAreaSx,
  getAdvancedPopoverRootSx,
  getAdvancedPopoverTitleSx,
  getAdvSearchPopoverPaperSx,
  getAdvSearchThreshold,
} from 'components/Search/styles';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { pick } from 'remeda';
import { useLogSearch } from './LogSearchProvider';
import { LogSearchPopoverFields } from './LogSearchFilterFields';

/**
 * Advanced-filters popover for the Log Search Modal. Stages Level + Date range
 * and applies them as a pure replace on "Apply", or clears them on "Clear"
 * (preserving the message query). Anchored to the input row and matched to its width.
 */
export const LogSearchAdvancedPopover = memo(() => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    advPopoverOpen,
    advPopoverAnchorEl,
    handleCloseAdvPopover,
    textBoxWidth,
    filters,
    setFilters,
    stagedFilters,
    setStagedFilters,
    keptKeys,
  } = useLogSearch();

  // Keep the message query when clearing the advanced (Level / Date) filters.
  const handleClearStagedFilters = useCallback(() => {
    setStagedFilters(pick(filters, keptKeys));
  }, [filters, keptKeys, setStagedFilters]);

  // Staged filters were seeded from the applied filters on open
  const handleApplyFilters = useCallback(() => {
    setFilters(stagedFilters);
    handleCloseAdvPopover();
  }, [setFilters, stagedFilters, handleCloseAdvPopover]);

  const popoverSlotProps: PopoverProps['slotProps'] = useMemo(
    () => ({
      root: { sx: { ...getAdvancedPopoverRootSx(theme) } },
      paper: {
        sx: {
          ...getAdvSearchPopoverPaperSx(theme),
          width: textBoxWidth + 5,
        },
      },
    }),
    [theme, textBoxWidth],
  );

  const headerSx: SxProps<Theme> = useMemo(
    () => ({
      gridArea: 'advancedheader',
      justifyContent: 'space-between',
      alignItems: 'center',
      ...getAdvancedPopoverHeaderAreaSx(theme),
    }),
    [theme],
  );

  const strClearAll = t('blox.actions.clear_all');
  const strApply = t('blox.actions.apply');
  const strAdvancedFilters = t('blox.navigation.assets.advanced_filters');
  const strCloseWithoutApplying = t('blox.general.close_without_applying');

  return (
    <Popover
      data-testid="log-search-advanced-popover"
      open={advPopoverOpen}
      anchorEl={advPopoverAnchorEl}
      onClose={handleCloseAdvPopover}
      elevation={advSearchElevation}
      marginThreshold={getAdvSearchThreshold(theme)}
      anchorOrigin={advSearchAnchorOrigin}
      transformOrigin={advSearchTransformOrigin}
      slotProps={popoverSlotProps}
    >
      <Stack
        direction="row"
        sx={headerSx}
      >
        <Typography sx={getAdvancedPopoverTitleSx}>{strAdvancedFilters}</Typography>
        <Tooltip
          title={strCloseWithoutApplying}
          disableInteractive
        >
          <IconButton
            data-testid="log-search-advanced-close"
            onClick={handleCloseAdvPopover}
            size="small"
          >
            <ClearIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack
        data-testid="log-search-advanced-popover-inputs"
        direction="column"
        sx={{ justifyContent: 'space-between', ...getAdvancedPopoverInputsAreaSx(theme) }}
      >
        <LogSearchPopoverFields />
      </Stack>

      <Stack
        direction="row"
        sx={{
          justifyContent: 'flex-end',
          alignItems: 'center',
          ...getAdvancedPopoverFooterAreaSx(theme),
        }}
      >
        <Button
          data-testid="log-search-clear"
          variant="outlined"
          size="small"
          onClick={handleClearStagedFilters}
          color="info"
          disableElevation
          sx={{ width: '100px' }}
        >
          {strClearAll}
        </Button>
        <Button
          data-testid="log-search-apply"
          variant="contained"
          color="primary"
          size="small"
          onClick={handleApplyFilters}
          disableElevation
          sx={{ width: '100px' }}
        >
          {strApply}
        </Button>
      </Stack>
    </Popover>
  );
});

LogSearchAdvancedPopover.displayName = 'LogSearchAdvancedPopover';
